import { useState, useEffect } from 'react';
import api from '../../api';
import { toast } from 'sonner';
import { Button } from '../../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Badge } from '../../components/ui/badge';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '../../components/ui/dialog';
import { Textarea } from '../../components/ui/textarea';
import { AlertTriangle, Package, RefreshCw, Loader2, Plus, Bell, Search } from 'lucide-react';
import Pagination from '../../components/Pagination';
import ProductImage from '../../components/ProductImage';

export default function AdminOutOfStock() {
  const [products, setProducts] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [filter, setFilter] = useState('all'); // 'all', 'out', 'low'
  const [searchTerm, setSearchTerm] = useState('');
  const [currentPage, setCurrentPage] = useState(1);
  const [selectedProduct, setSelectedProduct] = useState(null);
  const [showAddDialog, setShowAddDialog] = useState(false);
  const [licenseKeys, setLicenseKeys] = useState('');
  const [adding, setAdding] = useState(false);
  const itemsPerPage = 12;

  useEffect(() => { fetchProducts(); }, []);

  const fetchProducts = async () => {
    setIsLoading(true);
    try {
      const response = await api.get('/admin/stock-status');
      setProducts(response.data);
    } catch (error) {
      console.error('Failed to fetch stock status:', error);
      toast.error('Failed to fetch stock status');
    } finally {
      setIsLoading(false);
    }
  };

  const handleAddLicenses = async (e) => {
    e.preventDefault();
    if (!licenseKeys.trim() || !selectedProduct) return;

    setAdding(true);
    try {
      const keys = licenseKeys.split('\n').filter(k => k.trim());
      await api.post(`/admin/products/${selectedProduct.id}/licenses`, { keys });
      toast.success(`Added ${keys.length} license keys`);
      setShowAddDialog(false);
      setLicenseKeys('');
      setSelectedProduct(null);
      fetchProducts();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to add licenses');
    } finally {
      setAdding(false);
    }
  };

  const sendRestockReminder = async (product) => {
    try {
      await api.post('/admin/send-restock-reminder', { product_id: product.id });
      toast.success('Restock reminder sent via Telegram');
    } catch (error) {
      toast.error('Failed to send reminder');
    }
  };

  // Filter products
  const filteredProducts = products.filter(p => {
    const matchesSearch = p.title.toLowerCase().includes(searchTerm.toLowerCase());
    if (filter === 'out') return matchesSearch && p.stock === 0;
    if (filter === 'low') return matchesSearch && p.stock > 0 && p.stock <= 5;
    return matchesSearch;
  });

  // Pagination
  const totalPages = Math.ceil(filteredProducts.length / itemsPerPage);
  const paginatedProducts = filteredProducts.slice(
    (currentPage - 1) * itemsPerPage,
    currentPage * itemsPerPage
  );

  const outOfStockCount = products.filter(p => p.stock === 0).length;
  const lowStockCount = products.filter(p => p.stock > 0 && p.stock <= 5).length;

  if (isLoading) {
    return (
      <div className="flex justify-center py-12">
        <Loader2 className="w-8 h-8 animate-spin text-[#FFD700]" />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between flex-wrap gap-4">
        <div>
          <h1 className="text-2xl font-bold text-white font-['Outfit'] flex items-center gap-2">
            <AlertTriangle className="w-7 h-7 text-[#FFD700]" />
            Stock Management
          </h1>
          <p className="text-zinc-500">Monitor and manage product stock levels</p>
        </div>
        <Button onClick={fetchProducts} variant="outline" className="border-white/10">
          <RefreshCw className="w-4 h-4 mr-2" />Refresh
        </Button>
      </div>

      {/* Stats Cards */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <Card className="bg-[#0A0A0A] border-red-500/30">
          <CardContent className="p-4 flex items-center gap-4">
            <div className="w-12 h-12 rounded-lg bg-red-500/20 flex items-center justify-center">
              <AlertTriangle className="w-6 h-6 text-red-500" />
            </div>
            <div>
              <p className="text-2xl font-bold text-red-500">{outOfStockCount}</p>
              <p className="text-sm text-zinc-500">Out of Stock</p>
            </div>
          </CardContent>
        </Card>
        
        <Card className="bg-[#0A0A0A] border-yellow-500/30">
          <CardContent className="p-4 flex items-center gap-4">
            <div className="w-12 h-12 rounded-lg bg-yellow-500/20 flex items-center justify-center">
              <Package className="w-6 h-6 text-yellow-500" />
            </div>
            <div>
              <p className="text-2xl font-bold text-yellow-500">{lowStockCount}</p>
              <p className="text-sm text-zinc-500">Low Stock (≤5)</p>
            </div>
          </CardContent>
        </Card>
        
        <Card className="bg-[#0A0A0A] border-green-500/30">
          <CardContent className="p-4 flex items-center gap-4">
            <div className="w-12 h-12 rounded-lg bg-green-500/20 flex items-center justify-center">
              <Package className="w-6 h-6 text-green-500" />
            </div>
            <div>
              <p className="text-2xl font-bold text-green-500">{products.length - outOfStockCount - lowStockCount}</p>
              <p className="text-sm text-zinc-500">In Stock</p>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Filters */}
      <div className="flex flex-wrap gap-4 items-center">
        <div className="relative flex-1 max-w-sm">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-zinc-500" />
          <Input
            placeholder="Search products..."
            value={searchTerm}
            onChange={(e) => { setSearchTerm(e.target.value); setCurrentPage(1); }}
            className="pl-10 bg-[#121212] border-white/10 text-white"
          />
        </div>
        
        <div className="flex gap-2">
          <Button
            variant={filter === 'all' ? 'default' : 'outline'}
            onClick={() => { setFilter('all'); setCurrentPage(1); }}
            className={filter === 'all' ? 'bg-[#FFD700] text-black' : 'border-white/10'}
          >
            All ({products.length})
          </Button>
          <Button
            variant={filter === 'out' ? 'default' : 'outline'}
            onClick={() => { setFilter('out'); setCurrentPage(1); }}
            className={filter === 'out' ? 'bg-red-500 text-white' : 'border-red-500/30 text-red-500'}
          >
            Out of Stock ({outOfStockCount})
          </Button>
          <Button
            variant={filter === 'low' ? 'default' : 'outline'}
            onClick={() => { setFilter('low'); setCurrentPage(1); }}
            className={filter === 'low' ? 'bg-yellow-500 text-black' : 'border-yellow-500/30 text-yellow-500'}
          >
            Low Stock ({lowStockCount})
          </Button>
        </div>
      </div>

      {/* Products Grid */}
      {filteredProducts.length === 0 ? (
        <Card className="bg-[#0A0A0A] border-white/10">
          <CardContent className="p-12 text-center">
            <Package className="w-16 h-16 mx-auto text-zinc-600 mb-4" />
            <p className="text-zinc-400 text-lg">No products found</p>
            <p className="text-zinc-500 text-sm">
              {filter === 'out' ? 'All products have stock available!' : 
               filter === 'low' ? 'No products with low stock' : 
               'Add products to get started'}
            </p>
          </CardContent>
        </Card>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4">
          {paginatedProducts.map((product) => (
            <Card 
              key={product.id} 
              className={`bg-[#0A0A0A] border-2 ${
                product.stock === 0 ? 'border-red-500/50' : 
                product.stock <= 5 ? 'border-yellow-500/50' : 'border-white/10'
              }`}
            >
              <CardContent className="p-4">
                <div className="aspect-video rounded-lg overflow-hidden mb-3 bg-[#121212]">
                  <ProductImage 
                    src={product.thumbnail} 
                    alt={product.title}
                    className="w-full h-full object-cover"
                  />
                </div>
                
                <h3 className="font-semibold text-white truncate mb-1">{product.title}</h3>
                <p className="text-xs text-zinc-500 mb-3">{product.category_name}</p>
                
                <div className="flex items-center justify-between mb-3">
                  <Badge 
                    className={
                      product.stock === 0 ? 'bg-red-500/20 text-red-500' : 
                      product.stock <= 5 ? 'bg-yellow-500/20 text-yellow-500' : 
                      'bg-green-500/20 text-green-500'
                    }
                  >
                    {product.stock === 0 ? 'OUT OF STOCK' : `${product.stock} in stock`}
                  </Badge>
                  <span className="text-[#FFD700] font-bold">₦{product.price.toLocaleString()}</span>
                </div>

                <div className="flex gap-2">
                  <Dialog open={showAddDialog && selectedProduct?.id === product.id} onOpenChange={(open) => {
                    setShowAddDialog(open);
                    if (!open) { setSelectedProduct(null); setLicenseKeys(''); }
                  }}>
                    <DialogTrigger asChild>
                      <Button 
                        size="sm" 
                        className="flex-1 bg-[#FFD700] text-black hover:bg-[#E5C100]"
                        onClick={() => setSelectedProduct(product)}
                      >
                        <Plus className="w-4 h-4 mr-1" />Add Stock
                      </Button>
                    </DialogTrigger>
                    <DialogContent className="bg-[#0A0A0A] border-2 border-[#FFD700]/30">
                      <DialogHeader>
                        <DialogTitle className="text-white">Add License Keys - {product.title}</DialogTitle>
                      </DialogHeader>
                      <form onSubmit={handleAddLicenses} className="space-y-4">
                        <div>
                          <Label className="text-[#FFD700] text-xs uppercase">License Keys (one per line)</Label>
                          <Textarea
                            value={licenseKeys}
                            onChange={(e) => setLicenseKeys(e.target.value)}
                            placeholder="Enter license keys, one per line..."
                            className="bg-[#121212] border-white/10 text-white h-40"
                            required
                          />
                        </div>
                        <Button type="submit" disabled={adding} className="w-full bg-[#FFD700] text-black">
                          {adding ? <><Loader2 className="w-4 h-4 animate-spin mr-2" />Adding...</> : 'Add License Keys'}
                        </Button>
                      </form>
                    </DialogContent>
                  </Dialog>
                  
                  <Button 
                    size="sm" 
                    variant="outline"
                    onClick={() => sendRestockReminder(product)}
                    className="border-white/10"
                    title="Send restock reminder"
                  >
                    <Bell className="w-4 h-4" />
                  </Button>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      )}

      {/* Pagination */}
      {totalPages > 1 && (
        <Pagination
          currentPage={currentPage}
          totalPages={totalPages}
          onPageChange={setCurrentPage}
        />
      )}
    </div>
  );
}
